import {
  Banner,
  BannerDescription,
  BannerFootnote,
  BannerImage,
  BannerImageContainer,
  BannerLinks,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Button,
  Icon,
  List,
  ListItem,
  P,
  Spinner,
  Typography,
} from '@vp/swan'

import { isProduction } from '@vp/digital-environment-lib-ubik'

import { GET_STARTED_CTA_DETAIL, SEO_DATA_SECTION_LABEL, trackSeoSectionStartForFreeLinkClick } from '../WixWebsitesAnalytics'
import Markdown from 'markdown-to-jsx'
import { TokenReplacer, LinkFactory } from '@vp/digital-token-replacer-lib-ubik'
import { WIX_TERMS_OF_USE_PAGER_URL, WIX_PRIVACY_POLICY_PAGER_URL } from '@vp/digital-url-service-lib-ubik'
import MissingContentAlert from '../../../common/MissingContentAlert'
import { AccessibilityContent } from '../../../common/types/AccessibilityTypes'

interface SeoSectionProps {
  pageContext: any
  accessibility: AccessibilityContent
  isGetStartedLoading?: boolean
  handleGetStartedNavigation: () => void
}

const SeoSection = ({ pageContext, accessibility, isGetStartedLoading, handleGetStartedNavigation }: SeoSectionProps) => {
  const { seoComponentContent } = pageContext

  if (!seoComponentContent) {
    return <MissingContentAlert />
  }

  const { urlWixPartnerGetStartedTnCAcceptedUrlsMcs } = pageContext

  const urlWixPartnerGetStartedTncAcceptedUrl = isProduction() ? urlWixPartnerGetStartedTnCAcceptedUrlsMcs.prod : urlWixPartnerGetStartedTnCAcceptedUrlsMcs.qa

  return (
    <Banner
      variant="card"
      height="short"
      mb={{ sm: 5, md: 5, lg: 7, xl: 7 }}
      pr={{ sm: 0, md: 1, lg: 1, xl: 1 }}
      pl={{ sm: 0, md: 0, lg: 0, xl: 0 }}
      style={{ backgroundColor: '#f7f8f0' }}
    >
      <BannerTextContainer>
        <BannerText>
          <BannerTitle component="h2" fontSize="x2large">
            {seoComponentContent.title}
          </BannerTitle>

          <BannerDescription component="p">{seoComponentContent.description}</BannerDescription>
          <BannerDescription mt={6} textAlign="left">
            <P fontWeight="bold" fontSize="large">
              {seoComponentContent.heroFeatures.title}
            </P>

            <List skin="minimal" mt={4}>
              {seoComponentContent.heroFeatures.features.map((item: { name: string; description: string }) => (
                <ListItem key={item.name ?? item.description}>
                  <Icon iconType="check" size="24p" skin="standard" mr={4} />
                  <Markdown>{item.name}</Markdown>
                </ListItem>
              ))}
            </List>
          </BannerDescription>
          <BannerLinks>
            <Button
              skin="primary"
              data-position="4"
              data-translation={GET_STARTED_CTA_DETAIL}
              data-section={SEO_DATA_SECTION_LABEL}
              onClick={() => {
                trackSeoSectionStartForFreeLinkClick(urlWixPartnerGetStartedTncAcceptedUrl)
                handleGetStartedNavigation()
              }}
            >
              {seoComponentContent.actions[0].name}

              {isGetStartedLoading ? (
                <Spinner accessibleText={accessibility.features[15].name} className="wix-websites-pdp__cta__spinner light" />
              ) : (
                <Icon iconType="arrowRight" size="20p" />
              )}
            </Button>
          </BannerLinks>
          <BannerFootnote textColor="info">
            <Typography as="span">
              <TokenReplacer
                template={seoComponentContent.actions[0].description}
                replacements={{
                  link: LinkFactory({
                    wixTermsOfUsePagerUrl: WIX_TERMS_OF_USE_PAGER_URL,
                    wixPrivacyPolicyPagerUrl: WIX_PRIVACY_POLICY_PAGER_URL,
                  }),
                }}
                convertMarkdown
              />
            </Typography>
          </BannerFootnote>
        </BannerText>
      </BannerTextContainer>
      <BannerImageContainer p={2}>
        <BannerImage pt={3}>
          <video
            autoPlay
            loop
            playsInline
            muted
            style={{
              objectFit: 'contain',
            }}
          >
            <source src={seoComponentContent.media[0].mediaLink[0].secure_url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </BannerImage>
      </BannerImageContainer>
    </Banner>
  )
}

export default SeoSection
